import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  Link as MuiLink,
  TableCell,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material';
import { Check, Circle, Close } from '@mui/icons-material';
import { getDisplayUserName } from '../../../../../lib/userUtils';
import CustomInputField from '../../../../../ui/primitives/CustomInputField';
import CustomSelect from '../../../../../ui/primitives/CustomSelect';
import Modal from '../../../../../ui/layout/Modal';
import AttachmentTable from '../../../../../features/attachments/AttachmentTable';
import { attachmentTableHeaders } from '../../../test-case/testCaseTableHead';
import { renderLinks } from '../../../../../lib/utils';
import { AttachmentIcon } from '../../../../../ui/primitives/CustomIcons';

const CELL_SX = {
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
  verticalAlign: 'top',
  p: 1,
};

const TestStepItem = ({
  script,
  index,
  statuses,
  isEditing,
  defaultStatus,
  handleUpdateStep,
  loading,
  showUri,
  isSelected,
  onSelectRow,
  isRowDisabled,
}) => {
  const [editingField, setEditingField] = useState(null);
  const [tempActualResult, setTempActualResult] = useState('');
  const [openAttachments, setOpenAttachments] = useState(false);
  const currentStatus = script?.status_details || defaultStatus;
  const [order, setOrder] = useState('desc');

  const componentRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (componentRef.current && !componentRef.current.contains(event.target)) {
        handleCancelClick();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onDropdownChange = async (statusId) => {
    await handleUpdateStep({ status: statusId }, script.id);
    const selectedStatus = statuses.find((s) => s.id === statusId);
    if (selectedStatus?.name?.toLowerCase() === 'fail') {
      if (typeof script?.onStepFailed === 'function') {
        script.onStepFailed();
      }
    }
  };

  const onActualResultChange = (id, value) => setTempActualResult(value);

  const handleActualResultClick = () => {
    setEditingField(script.id);
    setTempActualResult(script?.actualResult || '');
  };

  const handleSaveClick = async () => {
    await handleUpdateStep({ actualResult: tempActualResult }, script.id);
    setEditingField(null);
  };

  const handleCancelClick = () => {
    setEditingField(null);
    setTempActualResult('');
  };

  const renderCell = (content, isLinkable = false) => (
    <TableCell sx={CELL_SX}>
      {isLinkable ? renderLinks(content || '') : content || 'None'}
    </TableCell>
  );

  const attachments = script?.teststeps?.attachments || [];
  const isActualResultEditable = isEditing;

  const renderStatusValue = (val) => {
    const s = statuses.find((st) => st.id === val);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {s && <Circle sx={{ color: s.color, fontSize: '0.875rem' }} />}
        <Typography variant="body2" component="span">
          {s?.name || 'Select Status'}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <TableRow ref={componentRef}>
        {isEditing && (
        <TableCell sx={{ width: 40, textAlign: 'center', p: 1 }}>
          <Checkbox
            size="small"
            checked={isSelected}
            disabled={isRowDisabled?.(script)}
            onChange={() => onSelectRow(script.id)}
            inputProps={{ 'aria-label': `select step ${script.teststeps?.s_no}` }}
          />
        </TableCell>
        )}

        {renderCell(script.teststeps?.s_no)}
        {renderCell(script.teststeps?.step, true)}
        {renderCell(script.teststeps?.test_data, true)}
        {renderCell(script.teststeps?.expected_result, true)}

        {showUri && (
          <TableCell sx={CELL_SX}>
            {script.teststeps?.uri ? (
              <MuiLink
                href={script.teststeps.uri}
                target="_blank"
                rel="noopener noreferrer"
                title="Open URI in new tab"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'underline',
                  '&:hover': { color: 'primary.dark' },
                }}
              >
                {script.teststeps.uri}
              </MuiLink>
            ) : (
              'None'
            )}
          </TableCell>
        )}

        <TableCell sx={CELL_SX}>
          {isActualResultEditable && editingField === script.id ? (
            <Box>
              <CustomInputField
                id="actualResult"
                value={tempActualResult}
                onChange={onActualResultChange}
                maxLength={500}
                placeholder="Enter actual result"
                multiline
                rows={3}
                autoFocus
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                <IconButton size="small" title="Save" onClick={handleSaveClick}>
                  <Check sx={{ color: 'success.main', fontSize: 18 }} />
                </IconButton>
                <IconButton size="small" title="Cancel" onClick={handleCancelClick}>
                  <Close sx={{ color: 'text.secondary', fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Tooltip
              title={
                isActualResultEditable ? 'Click to edit actual result' : 'Actual result'
              }
            >
            <Box
              onClick={isActualResultEditable ? handleActualResultClick : undefined}
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                p: 0.5,
                borderRadius: 1,
                border: '1px solid transparent',
                ...(isActualResultEditable && {
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    minHeight: 40,
                  },
                }),
              }}
            >
              {script?.actual_result ? (
                renderLinks(script.actual_result)
              ) : isActualResultEditable ? (
                <Typography variant="caption" color="text.disabled">
                  Click to add actual result
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  None
                </Typography>
              )}
            </Box>
            </Tooltip>
          )}
        </TableCell>

        <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>
          {isEditing ? (
            <Box sx={{ minWidth: 170 }}>
              <CustomSelect
                id={`status-${script.id}`}
                value={currentStatus?.id || ''}
                onChange={onDropdownChange}
                options={statuses}
                disabled={loading}
                renderValue={renderStatusValue}
                renderOption={(opt) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Circle sx={{ color: opt.color, fontSize: '0.875rem' }} />
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        fontWeight: opt.id === currentStatus?.id ? 600 : 400,
                      }}
                    >
                      {opt.name}
                    </Typography>
                  </Box>
                )}
              />
            </Box>
          ) : (
            <Box
              sx={{
                color: 'common.white',
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                textAlign: 'center',
                fontSize: '0.8125rem',
                fontWeight: 500,
                backgroundColor: script.status_details?.color || 'transparent',
              }}
            >
              {script.status_details?.name || 'No Status'}
            </Box>
          )}
        </TableCell>

        <TableCell sx={{ p: 1 }}>
          {attachments?.length > 0 && (
            <Tooltip title="View Attachments">
              <AttachmentIcon onClick={() => setOpenAttachments(true)} />
            </Tooltip>
          )}
        </TableCell>
      </TableRow>

      <Modal
        open={openAttachments}
        handleClose={() => setOpenAttachments(false)}
        width="lg"
        modalTitle={`Test Step Attachments - Step ${script?.teststeps?.s_no}`}
      >
        <Box sx={{ px: 0 }}>
          <AttachmentTable
            data={
              attachments.map((att) => ({
                ...att,
                owner: getDisplayUserName(att?.user_detail),
                file_name: att.file_name.split('.')[0],
              }))
            }
            headerTabs={attachmentTableHeaders}
            isLoading={loading}
            defaultSort="created_at"
            order={order}
            setOrder={setOrder}
            editableFields={['file_name']}
            isEditable={false}
          />
        </Box>
      </Modal>
    </>
  );
};

export default TestStepItem;